import React, { useState, useRef, useEffect } from "react";
import { X, Sparkles, CornerDownLeft } from "lucide-react";
import { streamAsk } from "@/lib/api";

const SUGGESTIONS = [
  "What happened in the Gardner Museum heist?",
  "How was Han van Meegeren caught?",
  "Why is provenance so important in forgery cases?",
  "What is the most famous unrecovered painting?",
];

export function AskArchive({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const idRef = useRef(0);
  const nextId = () => ++idRef.current;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const ask = async (question) => {
    if (!question.trim() || busy) return;
    setInput("");
    setMessages((m) => [...m, { id: nextId(), role: "user", text: question }, { id: nextId(), role: "assistant", text: "" }]);
    setBusy(true);
    await streamAsk(
      question,
      sessionRef.current,
      (delta) =>
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], text: copy[copy.length - 1].text + delta };
          return copy;
        }),
      (sid) => { sessionRef.current = sid; setBusy(false); },
      (err) =>
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], text: `The archivist is unavailable. (${err})` };
          return copy;
        })
    );
    setBusy(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="ask-archive-panel">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-surface border-l border-line flex flex-col fade-up">
        <div className="flex items-center justify-between border-b border-line px-6 py-5">
          <div className="flex items-center gap-2 text-gold">
            <Sparkles size={16} />
            <span className="eyebrow text-gold">Ask the Archive</span>
          </div>
          <button onClick={onClose} data-testid="close-ask-archive" aria-label="Close" className="text-faint hover:text-gold">
            <X size={20} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
          {messages.length === 0 && (
            <div>
              <p className="font-serif text-2xl leading-snug text-parchment">
                Ask the archivist about any case, artist, forgery, theft, restitution, or legal battle in the history of art.
              </p>
              <div className="mt-8 space-y-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid="ask-suggestion"
                    onClick={() => ask(s)}
                    className="block w-full border border-line px-4 py-3 text-left text-sm text-parchment/80 hover:border-gold/50 hover:text-gold transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-6">
            {messages.map((m) =>
              m.role === "user" ? (
                <div key={m.id} className="text-right">
                  <p className="inline-block max-w-[85%] bg-elevated border border-line px-4 py-2 font-mono text-xs text-gold">
                    {m.text}
                  </p>
                </div>
              ) : (
                <div key={m.id}>
                  <div className="eyebrow mb-2 text-faint">Archivist</div>
                  <p className="whitespace-pre-wrap font-serif text-lg leading-relaxed text-parchment">
                    {m.text || (busy ? "…" : "")}
                  </p>
                </div>
              )
            )}
          </div>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); ask(input); }}
          className="border-t border-line p-4"
        >
          <div className="flex items-end gap-2 border border-line bg-ink px-3 py-2 focus-within:border-gold/50">
            <textarea
              data-testid="ask-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(input); } }}
              rows={1}
              placeholder="Pose a question…"
              className="max-h-32 flex-1 resize-none bg-transparent py-1 text-sm text-parchment placeholder:text-faint focus:outline-none"
            />
            <button
              type="submit"
              data-testid="ask-submit"
              disabled={busy || !input.trim()}
              className="p-1 text-gold disabled:text-faint"
              aria-label="Send"
            >
              <CornerDownLeft size={18} />
            </button>
          </div>
          <p className="mt-2 font-mono text-[10px] text-faint">Grounded in the COA archive · answers may be summarized</p>
        </form>
      </aside>
    </div>
  );
}
